import type { Variants } from '@amplitude/experiment-js-client';

import { memo, ReactNode, useEffect, useState } from 'react';

import {
    AmplitudeExperimentsContextType,
    AmplitudeExperimentsProvider,
} from '@/components/AmplitudeExperimentsProvider/Context';

// overriddenFlagKeys is derived by the provider from the overrides — callers must not pass it
type AmplitudeExperimentsClientProviderProps = Omit<AmplitudeExperimentsContextType, 'overriddenFlagKeys'> & {
    children: ReactNode;
    /** Non-production forceExperiment overrides resolved at client boot (see forcedExperimentOverrides.ts). */
    forcedVariantOverrides?: Variants;
};

export const AmplitudeExperimentsClientProvider = memo(
    ({
        children,
        client,
        deviceId,
        forcedVariantOverrides,
        userId,
        variants,
    }: AmplitudeExperimentsClientProviderProps) => {
        // Apply the override only after mount so the hydration render matches the server-rendered
        // HTML (the server resolves the real variants and knows nothing about the override).
        const [variantOverrides, setVariantOverrides] = useState<Variants | undefined>(undefined);

        useEffect(() => {
            if (forcedVariantOverrides && Object.keys(forcedVariantOverrides).length > 0) {
                setVariantOverrides(forcedVariantOverrides);
            }
        }, [forcedVariantOverrides]);

        return (
            <AmplitudeExperimentsProvider
                client={client}
                deviceId={deviceId}
                userId={userId}
                variantOverrides={variantOverrides}
                variants={variants}
            >
                {children}
            </AmplitudeExperimentsProvider>
        );
    }
);

AmplitudeExperimentsClientProvider.displayName = 'AmplitudeExperimentsClientProvider';
